import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Briefcase, Users, DollarSign, Clock, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { submitApplication } from '../api/client';

const CareersPage = () => {
  const [applicationData, setApplicationData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    experience: '',
    availability: '',
    whyJoin: '',
    references: '',
    resume: null
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      // Check file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload a PDF or Word document');
        return;
      }
      setApplicationData({ ...applicationData, resume: file });
      toast.success('Resume uploaded successfully');
    }
  };

  const removeResume = () => {
    setApplicationData({ ...applicationData, resume: null });
    // Reset file input
    const fileInput = document.getElementById('resume');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('firstName', applicationData.firstName);
      formData.append('lastName', applicationData.lastName);
      formData.append('email', applicationData.email);
      formData.append('phone', applicationData.phone);
      formData.append('address', applicationData.address);
      formData.append('experience', applicationData.experience);
      formData.append('availability', applicationData.availability);
      formData.append('whyJoin', applicationData.whyJoin);
      formData.append('references', applicationData.references);
      
      if (applicationData.resume) {
        formData.append('resume', applicationData.resume);
      }
      
      await submitApplication(formData);
      toast.success('Application received! We will review your information and contact you soon.');
      
      setApplicationData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        experience: '',
        availability: '',
        whyJoin: '',
        references: '',
        resume: null
      });
      
      // Reset file input
      const fileInput = document.getElementById('resume');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
      console.error('Application error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: DollarSign,
      title: 'Competitive Pay',
      description: 'Fair compensation with opportunities for raises and bonuses'
    },
    {
      icon: Clock,
      title: 'Flexible Schedule',
      description: 'Work hours that fit your lifestyle and commitments'
    },
    {
      icon: Users,
      title: 'Great Team',
      description: 'Join a supportive and professional work environment'
    },
    {
      icon: Briefcase,
      title: 'Career Growth',
      description: 'Advancement opportunities and professional development'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Join Our <span className="text-red-600">Team</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Be part of a company with over 40 years of excellence. We're always looking for dedicated professionals.
            </p>
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Work With Taylormade?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We value our employees and provide a supportive environment for success
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="border-gray-200 text-center hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{benefit.title}</h3>
                    <p className="text-gray-600 text-sm">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* What We're Looking For */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">What We're Looking For</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle>Requirements</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Reliable transportation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Attention to detail</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Professional attitude</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Physical ability to perform cleaning tasks</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle>Preferred Qualifications</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Previous cleaning experience</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Customer service skills</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Time management abilities</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Team player mentality</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">•</span>
                    <span>Flexible availability</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-gray-200">
            <CardHeader>
              <CardTitle className="text-2xl">Employment Application</CardTitle>
              <CardDescription>
                Fill out the form below and we'll get back to you soon
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Information */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Personal Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input
                        id="firstName"
                        required
                        value={applicationData.firstName}
                        onChange={(e) => setApplicationData({ ...applicationData, firstName: e.target.value })}
                        placeholder="John"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input
                        id="lastName"
                        required
                        value={applicationData.lastName}
                        onChange={(e) => setApplicationData({ ...applicationData, lastName: e.target.value })}
                        placeholder="Doe"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={applicationData.email}
                        onChange={(e) => setApplicationData({ ...applicationData, email: e.target.value })}
                        placeholder="john@example.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        required
                        value={applicationData.phone}
                        onChange={(e) => setApplicationData({ ...applicationData, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address">Address *</Label>
                    <Input
                      id="address"
                      required
                      value={applicationData.address}
                      onChange={(e) => setApplicationData({ ...applicationData, address: e.target.value })}
                      placeholder="123 Main St, Lexington, KY"
                    />
                  </div>
                </div>

                {/* Experience & Availability */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Experience & Availability</h3>
                  <div className="space-y-2">
                    <Label htmlFor="experience">Previous Cleaning Experience</Label>
                    <Textarea
                      id="experience"
                      rows={4}
                      value={applicationData.experience}
                      onChange={(e) => setApplicationData({ ...applicationData, experience: e.target.value })}
                      placeholder="Describe your previous cleaning or related work experience..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="availability">Availability *</Label>
                    <Textarea
                      id="availability"
                      required
                      rows={3}
                      value={applicationData.availability}
                      onChange={(e) => setApplicationData({ ...applicationData, availability: e.target.value })}
                      placeholder="What days and hours are you available to work?"
                    />
                  </div>
                </div>

                {/* Additional Information */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Additional Information</h3>
                  
                  {/* Resume Upload */}
                  <div className="space-y-2">
                    <Label htmlFor="resume">Upload Resume (Optional)</Label>
                    <div className="flex flex-col gap-3">
                      {!applicationData.resume ? (
                        <div className="relative">
                          <Input
                            id="resume"
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleFileChange}
                            className="cursor-pointer"
                          />
                          <p className="text-sm text-gray-500 mt-1">
                            Accepted formats: PDF, DOC, DOCX (Max 5MB)
                          </p>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md">
                          <div className="flex items-center gap-2">
                            <Upload className="w-5 h-5 text-green-600" />
                            <span className="text-sm font-medium text-green-700">
                              {applicationData.resume.name}
                            </span>
                            <span className="text-xs text-gray-500">
                              ({(applicationData.resume.size / 1024).toFixed(1)} KB)
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={removeResume}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="whyJoin">Why do you want to join Taylormade? *</Label>
                    <Textarea
                      id="whyJoin"
                      required
                      rows={4}
                      value={applicationData.whyJoin}
                      onChange={(e) => setApplicationData({ ...applicationData, whyJoin: e.target.value })}
                      placeholder="Tell us why you're interested in working with us..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="references">References</Label>
                    <Textarea
                      id="references"
                      rows={4}
                      value={applicationData.references}
                      onChange={(e) => setApplicationData({ ...applicationData, references: e.target.value })}
                      placeholder="Please provide 2-3 professional references with contact information..."
                    />
                  </div>
                </div>

                <Button type="submit" disabled={isSubmitting} className="bg-red-600 hover:bg-red-700 text-white w-full" size="lg">
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
